/**
 * Tolerant money parsing for amount inputs.
 *
 * The system displays amounts in en-US format (`1,000.00` — comma thousands, dot
 * decimal), but data-entry should accept either separator so users can type/paste
 * `1500.50`, `1500,50`, `1,500.50` or `1.500,50` and get the right number.
 *
 * Rule: the LAST separator that appears (`.` or `,`) is treated as the decimal
 * point; every other `.`/`,` is dropped as a thousands separator. This is robust
 * for live typing of either separator (e.g. while typing `1500,5` the comma is the
 * last separator → 1500.5). The only ambiguous case is a lone thousands separator
 * with no decimals (`1,500` → 1.5); in tolerant mode that trade-off is accepted.
 *
 * NOTE: bank-file imports in ConciliacionBancaria use their own parser, which keeps
 * a "2-digits-after-comma = decimal" heuristic better suited to CSV pastes.
 */

/** Parse a tolerant money string into a Number (returns 0 on empty/invalid). */
export function parseMoney(v) {
  if (v == null || v === "") return 0;
  if (typeof v === "number") return v;
  let s = String(v).replace(/[^\d.,-]/g, "");
  if (!s || s === "-") return 0;
  const lastComma = s.lastIndexOf(",");
  const lastDot   = s.lastIndexOf(".");
  const lastSep   = Math.max(lastComma, lastDot);
  if (lastSep > -1) {
    const intPart  = s.slice(0, lastSep).replace(/[.,]/g, "");
    const decPart  = s.slice(lastSep + 1).replace(/[.,]/g, "");
    s = `${intPart}.${decPart}`;
  }
  return Number(s) || 0;
}

/** Keep only the characters valid while typing a money amount (digits, separators, sign). */
export function sanitizeMoneyTyping(v) {
  return String(v).replace(/[^\d.,-]/g, "");
}
